<template>
  <NavBar pageName="Settings"></NavBar>

  <v-container style="margin-top: 185px;">
    <v-overlay :model-value="loading" class="align-center justify-center">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <div class="d-flex align-center mb-4">
      <v-btn icon variant="text" size="small" @click="$router.push('/settings/imsi')" class="mr-2">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <h2>IMSI Types</h2>
    </div>

    <v-row class="align-center mb-4">
      <v-col cols="12" md="6">
        <p class="text-caption text-grey">Manage IMSI types used for categorizing IMSIs</p>
      </v-col>
      <v-col cols="12" md="6" class="d-flex justify-end gap-2">
        <v-btn color="primary" variant="outlined" :loading="loading" @click="loadData">
          <v-icon class="mr-2">mdi-refresh</v-icon>
          Refresh
        </v-btn>
        <v-btn color="success" @click="openAddDialog">
          <v-icon class="mr-2">mdi-plus</v-icon>
          Add IMSI Type
        </v-btn>
      </v-col>
    </v-row>

    <v-card>
      <v-card-text>
        <v-data-table
          :headers="tableHeaders"
          :items="imsiTypes"
          :loading="loading"
          :items-per-page="20"
          item-value="id"
          class="elevation-1"
        >
          <template #[`item.imsis_count`]="{ item }">
            <v-chip size="small" variant="tonal">{{ item.imsis_count }}</v-chip>
          </template>
          <template #[`item.actions`]="{ item }">
            <v-btn icon variant="text" size="small" @click="openEditDialog(item)" color="primary">
              <v-icon size="small">mdi-pencil</v-icon>
            </v-btn>
            <v-btn icon variant="text" size="small" @click="confirmDelete(item)" color="error">
              <v-icon size="small">mdi-delete</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Add/Edit Dialog -->
    <ImsiTypeDialog
      v-model="showDialog"
      :editItem="editItem"
      @save="handleSave"
    />

    <!-- Delete Confirmation -->
    <v-dialog v-model="showDeleteDialog" max-width="400">
      <v-card>
        <v-card-title>Confirm Delete</v-card-title>
        <v-card-text>
          Are you sure you want to delete <strong>{{ deleteTarget?.name }}</strong>?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showDeleteDialog = false">Cancel</v-btn>
          <v-btn color="error" variant="flat" @click="handleDelete">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000" location="bottom right">
      {{ snackbar.message }}
    </v-snackbar>
  </v-container>
</template>

<script>
import { defineComponent } from 'vue';
import NavBar from "@/components/NavBar.vue";
import ImsiTypeDialog from "@/components/dialogs/ImsiTypeDialog.vue";
import { getImsiTypes, addImsiType, updateImsiType, deleteImsiType } from "@/composables/useImsiTypesAPI";
import store from "@/store";

export default defineComponent({
  name: 'ImsiTypesSettings',
  components: { NavBar, ImsiTypeDialog },
  data() {
    return {
      loading: false,
      imsiTypes: [],
      showDialog: false,
      editItem: null,
      showDeleteDialog: false,
      deleteTarget: null,
      snackbar: { show: false, message: '', color: 'success' },
      tableHeaders: [
        { title: 'ID', key: 'id', width: '80px' },
        { title: 'Name', key: 'name' },
        { title: 'IMSIs', key: 'imsis_count', width: '100px' },
        { title: 'Actions', key: 'actions', sortable: false, width: '120px' },
      ],
    };
  },
  methods: {
    async loadData() {
      this.loading = true;
      const token = store.getters.getToken;
      const res = await getImsiTypes(token);
      if (!res.error) {
        this.imsiTypes = res.imsi_types || [];
      } else {
        this.showSnackbar(res.message, 'error');
      }
      this.loading = false;
    },
    openAddDialog() {
      this.editItem = null;
      this.showDialog = true;
    },
    openEditDialog(item) {
      this.editItem = { ...item };
      this.showDialog = true;
    },
    async handleSave(form) {
      this.loading = true;
      const token = store.getters.getToken;
      let res;
      if (form.id) {
        res = await updateImsiType(token, form);
      } else {
        res = await addImsiType(token, form);
      }
      if (!res.error) {
        this.showSnackbar(res.message, 'success');
        await this.loadData();
      } else {
        this.showSnackbar(res.message, 'error');
      }
      this.loading = false;
    },
    confirmDelete(item) {
      this.deleteTarget = item;
      this.showDeleteDialog = true;
    },
    async handleDelete() {
      this.showDeleteDialog = false;
      this.loading = true;
      const token = store.getters.getToken;
      const res = await deleteImsiType(token, this.deleteTarget.id);
      if (!res.error) {
        this.showSnackbar(res.message, 'success');
        await this.loadData();
      } else {
        this.showSnackbar(res.message, 'error');
      }
      this.loading = false;
    },
    showSnackbar(message, color) {
      this.snackbar = { show: true, message, color };
    }
  },
  mounted() {
    this.loadData();
  }
});
</script>
